import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api';

/**
 * Shared query client. Client errors (4xx) are never retried — retrying a validation or
 * permission failure only delays the message the user needs to see.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

export const queryKeys = {
  authStatus: ['auth', 'status'] as const,
  me: ['me'] as const,
  members: ['workspace', 'members'] as const,
  invites: ['workspace', 'invites'] as const,
  connections: ['connections'] as const,
  catalogSources: ['catalog', 'sources'] as const,
  catalogAttributes: (sources: string[], enrichment: boolean) => ['catalog', 'attributes', sources.join(','), enrichment] as const,
  projects: ['projects'] as const,
  project: (id: string) => ['projects', id] as const,
  projectRuns: (id: string) => ['projects', id, 'runs'] as const,
  job: (id: string) => ['jobs', id] as const,
  jobEvents: (id: string) => ['jobs', id, 'events'] as const,
  accountFields: (projectId?: string) => ['accounts', 'fields', projectId ?? 'all'] as const,
  accounts: (key: unknown) => ['accounts', 'query', key] as const,
  duplicates: (projectId?: string) => ['duplicates', projectId ?? 'all'] as const,
  account: (id: string) => ['accounts', id] as const,
  people: (key: unknown) => ['people', 'query', key] as const,
  savedViews: (projectId?: string) => ['views', projectId ?? 'all'] as const,
  presets: ['presets'] as const,
  analytics: (projectId?: string) => ['analytics', projectId ?? 'all'] as const,
  assistantHistory: (projectId: string) => ['assistant', projectId] as const,
  quickStartReady: ['quick-start', 'ready'] as const,
  exports: ['exports'] as const,
  outreach: (key: unknown) => ['outreach', key] as const,
};
